import type { ComponentConfig } from "@puckeditor/core"
import { ReactNode } from "react"
import { SectionBase, sectionStyle } from "../lib/section"

export type RichTextProps = SectionBase & {
  /**
   * Lightweight article markup: paragraphs separated by blank lines, lines
   * starting with "## " render as h2 and "- " lines become list items.
   * **bold** spans are supported inside paragraphs.
   */
  text: string
}

/**
 * Minimal inline renderer for **bold** spans. Everything else renders as
 * plain text, so the storefront never receives raw HTML from the editor.
 */
function renderInline(text: string): (string | ReactNode)[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={i}>{part.slice(2, -2)}</strong>
    }
    return part
  })
}

/**
 * Article body block for blog posts. Compared to Text it supports h2
 * subheadings ("## Line") and bullet lists ("- Line"), which is what a
 * long-read article needs beyond plain paragraphs.
 */
export const RichText: ComponentConfig<RichTextProps> = {
  fields: {
    text: {
      type: "textarea",
      label: "Article text (blank line = new paragraph, ## = subheading, - = list item)",
    },
    background: {
      type: "radio",
      options: [
        { value: "none", label: "Transparent" },
        { value: "muted", label: "Muted" },
        { value: "accent", label: "Dark" },
      ],
    },
    paddingY: {
      type: "radio",
      options: [
        { value: "none", label: "None" },
        { value: "sm", label: "Small" },
        { value: "md", label: "Medium" },
        { value: "lg", label: "Large" },
      ],
    },
  },

  defaultProps: {
    text: "Write the article body here.\n\n## Subheading\n\n- First point\n- Second point",
    background: "none",
    paddingY: "md",
  },

  render: ({ text, background, paddingY }) => {
    const s = sectionStyle({ background, paddingY })
    const blocks = (text || "").split(/\n\s*\n/)

    return (
      <section style={s}>
        <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 24px" }}>
          {blocks.map((block, i) => {
            const lines = block.split("\n").filter((l) => l.trim() !== "")

            if (lines.length === 0) return null

            if (lines.every((l) => l.trimStart().startsWith("- "))) {
              return (
                <ul
                  key={i}
                  style={{ fontSize: "1.0625rem", lineHeight: 1.8, color: "#374151", margin: "0 0 16px", paddingLeft: 24 }}
                >
                  {lines.map((line, j) => (
                    <li key={j} style={{ marginBottom: 4 }}>
                      {renderInline(line.trimStart().slice(2))}
                    </li>
                  ))}
                </ul>
              )
            }

            if (lines[0].startsWith("## ")) {
              return (
                <h2 key={i} style={{ fontSize: "1.5rem", fontWeight: 700, lineHeight: 1.3, margin: "32px 0 12px", color: "#111827" }}>
                  {lines[0].slice(3)}
                </h2>
              )
            }

            return (
              <p key={i} style={{ fontSize: "1.0625rem", lineHeight: 1.8, color: "#374151", marginBottom: 16 }}>
                {renderInline(lines.join(" "))}
              </p>
            )
          })}
        </div>
      </section>
    )
  },
}
